import React from 'react';
import styled from 'styled-components';
import { Trophy, Medal, Award, Folder, Building2 } from 'lucide-react';
import { TopPerformer } from '../../services/dashboardService';

interface TopPerformersWidgetProps {
  performers: TopPerformer[];
}

const Container = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  padding: 28px;
  margin-bottom: 28px;

  @media (max-width: 767px) {
    padding: 16px;
    border-radius: 12px;
    margin-bottom: 20px;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 4px;
`;

const SectionIcon = styled.div`
  display: flex;
  align-items: center;
  color: var(--primary-color);
  filter: saturate(0.7) brightness(0.9);
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0 0 20px 0;
`;

const TableContainer = styled.div`
  background: var(--background-color);
  border-radius: 10px;
  padding: 8px;
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  padding: 12px 16px;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary);
  border-bottom: 1px solid var(--border-color);
  white-space: nowrap;

  &:first-child {
    text-align: right;
  }
`;

const Td = styled.td`
  padding: 14px 16px;
  text-align: center;
  font-size: 14px;
  color: var(--text-primary);
  border-bottom: 1px solid var(--border-color);
`;

const Tr = styled.tr`
  transition: background-color 0.15s ease;

  &:hover td {
    background: white;
  }

  &:last-child td {
    border-bottom: none;
  }
`;

const NameCell = styled(Td)`
  text-align: right;
`;

const NameWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const RankingBadge = styled.div<{ $rank: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  font-weight: 700;
  font-size: 14px;
  color: ${props => {
    if (props.$rank === 1) return 'var(--warning-color)';
    if (props.$rank === 2) return '#6b7280';
    if (props.$rank === 3) return '#92400e';
    return 'var(--text-secondary)';
  }};
  filter: saturate(0.7) brightness(0.9);
`;

const NameText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const PerformerName = styled.span`
  font-weight: 600;
  color: var(--text-primary);
`;

const DepartmentName = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--text-secondary);
`;

const CountValue = styled.span<{ $muted?: boolean }>`
  font-weight: 600;
  color: ${props => (props.$muted ? 'var(--text-secondary)' : 'var(--text-primary)')};
`;

const TotalCell = styled(Td)`
  min-width: 160px;
`;

const TotalBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const BarBackground = styled.div`
  flex: 1;
  height: 8px;
  background: var(--border-color);
  border-radius: 4px;
  overflow: hidden;
`;

const BarFill = styled.div<{ $percentage: number }>`
  height: 100%;
  width: ${props => Math.min(Math.max(props.$percentage, 4), 100)}%;
  background: var(--primary-color);
  border-radius: 4px;
  transition: width 0.3s ease;
  filter: saturate(0.7) brightness(0.9);
`;

const TotalText = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary);
  min-width: 28px;
  text-align: left;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 32px;
  color: var(--text-secondary);
  font-size: 14px;
`;

const getRankingIcon = (rank: number): React.ReactNode => {
  if (rank === 1) return <Trophy size={16} />;
  if (rank === 2) return <Medal size={16} />;
  if (rank === 3) return <Award size={16} />;
  return rank;
};

const TopPerformersWidget: React.FC<TopPerformersWidgetProps> = ({ performers }) => {
  // מיון יורד לפי סך היצירות (הגנתי — השרת מחזיר כבר ממוין)
  const ranked = [...performers].sort((a, b) => b.totalCreated - a.totalCreated);
  const maxTotal = Math.max(1, ...ranked.map(p => p.totalCreated));

  return (
    <Container>
      <SectionHeader>
        <SectionIcon>
          <Trophy size={20} />
        </SectionIcon>
        <SectionTitle>מובילי החודש</SectionTitle>
      </SectionHeader>
      <Subtitle>30 הימים האחרונים — לפי מספר תיקים שנוצרו</Subtitle>

      {ranked.length === 0 ? (
        <EmptyState>אין נתונים להצגה</EmptyState>
      ) : (
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <Th>שם</Th>
                <Th>תיקים</Th>
                <Th>סה"כ</Th>
              </tr>
            </thead>
            <tbody>
              {ranked.map((performer, index) => {
                const rank = index + 1;
                return (
                  <Tr key={performer.userId}>
                    <NameCell>
                      <NameWrap>
                        <RankingBadge $rank={rank}>{getRankingIcon(rank)}</RankingBadge>
                        <NameText>
                          <PerformerName>{performer.userName}</PerformerName>
                          {performer.departmentName && (
                            <DepartmentName>
                              <Building2 size={12} />
                              {performer.departmentName}
                            </DepartmentName>
                          )}
                        </NameText>
                      </NameWrap>
                    </NameCell>
                    <Td>
                      <CountValue $muted>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
                          <Folder size={14} />
                          {performer.clientsCreated}
                        </span>
                      </CountValue>
                    </Td>
                    <TotalCell>
                      <TotalBar>
                        <BarBackground>
                          <BarFill $percentage={(performer.totalCreated / maxTotal) * 100} />
                        </BarBackground>
                        <TotalText>{performer.totalCreated}</TotalText>
                      </TotalBar>
                    </TotalCell>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};

export default TopPerformersWidget;
